"use client";

/**
 * RoleGuard Component (HUMANDA)
 * REQ-036: Role-based access control for marketplace routes
 *
 * Guards routes by checking user.userType (camelCase) against allowed roles.
 * This is distinct from TierGuard which guards by tier number (minTier).
 *
 * Use cases:
 * - Marketplace: only buyer, executive, admin roles
 * - Broker Dashboard: only broker, executive, admin roles
 *
 * Behavior:
 * - Unauthenticated (no user): redirect to /login
 * - Authenticated but wrong role: redirect to /dashboard
 * - Authenticated with allowed role: render children
 *
 * @apal/web-humanda
 */

import type * as React from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@apal/shared-auth";

/**
 * RoleGuard props interface
 */
export interface RoleGuardProps {
  /** Array of allowed user types (e.g., ['buyer', 'executive', 'admin']) */
  allowedRoles: string[];
  /** Content to render when user has the required role */
  children: React.ReactNode;
  /** Content to render while loading auth state (optional) */
  loading?: React.ReactNode;
}

/**
 * RoleGuard - Protects content based on user role (userType).
 *
 * @example
 * ```tsx
 * <RoleGuard allowedRoles={['buyer', 'executive', 'admin']}>
 *   <MarketplacePage />
 * </RoleGuard>
 * ```
 */
export function RoleGuard({
  allowedRoles,
  children,
  loading,
}: RoleGuardProps): React.JSX.Element | null {
  const { isAuthenticated, isLoading, user } = useAuth();

  // Show loading state while auth is resolving
  if (isLoading) {
    if (loading) {
      return <>{loading}</>;
    }
    return null;
  }

  // Not authenticated or no user - redirect to login
  if (!isAuthenticated || !user) {
    return <Navigate to="/login" replace />;
  }

  // Authenticated but wrong role - redirect to dashboard
  if (!allowedRoles.includes(user.userType)) {
    return <Navigate to="/dashboard" replace />;
  }

  // User has allowed role - render children
  return <>{children}</>;
}

export default RoleGuard;
